// src/hooks/useCommentBadges.js
// ---------------------------------------------------------------------------
// Hook: renders small gold badge circles (with comment counts) on the
// top-right corner of every Fabric object that has comments, using the
// canvas `after:render` event so badges are drawn on top of objects.
//
// Usage:
//   useCommentBadges(canvas, commentCounts);
//     commentCounts = { [collabId]: number }
// ---------------------------------------------------------------------------
import { useEffect, useRef } from 'react';

/**
 * @param {fabric.Canvas|null} canvas
 * @param {Object.<string, number>} commentCounts — { collabId: count }
 */
export default function useCommentBadges(canvas, commentCounts) {
  const countsRef = useRef(commentCounts);
  countsRef.current = commentCounts;

  useEffect(() => {
    if (!canvas) return;

    const drawBadges = () => {
      const ctx    = canvas.getContext();
      const counts = countsRef.current;
      if (!counts || Object.keys(counts).length === 0) return;

      canvas.getObjects().forEach((obj) => {
        const cid = obj._collabId;
        if (!cid || !counts[cid]) return;

        const count = counts[cid];
        const bound = obj.getBoundingRect(); // rendering-pixel coords

        // Badge position: top-right corner of the bounding box
        const bx = bound.left + bound.width - 4;
        const by = bound.top - 4;
        const radius = 9;

        ctx.save();
        // Reset transform so we draw in screen px (getBoundingRect is already screen px)
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // Gold circle
        ctx.beginPath();
        ctx.arc(bx, by, radius, 0, Math.PI * 2);
        ctx.fillStyle = '#D4AF37';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Count text
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(count > 99 ? '99+' : String(count), bx, by + 0.5);
        ctx.restore();
      });
    };

    canvas.on('after:render', drawBadges);
    return () => canvas.off('after:render', drawBadges);
  }, [canvas]);
}
